<template>
    <div
        v-if="type == 'normal'"
        class="rounded-md p-4"
        :class="`${colorScheme.background} ${accent ? `border-1-4 ${colorScheme.accent}` : ''}`"
    >
        <div class="flex">
            <div class="shrink-0">
                <ExclamationTriangleIcon
                    class="h-5 w-5"
                    :class="colorScheme.icon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3">
                <h3
                    class="text-sm font-medium"
                    :class="colorScheme.title"
                >
                    {{ title }}
                </h3>
                <div
                    class="mt-2 text-sm"
                    :class="colorScheme.mainText"
                >
                    <p>{{ message }}</p>
                </div>
            </div>
        </div>
    </div>
    <div
        v-if="type == 'list'"
        class="rounded-md p-4"
        :class="colorScheme.background"
    >
        <div class="flex">
            <div class="shrink-0">
                <XCircleIcon
                    class="h-5 w-5"
                    :class="colorScheme.icon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3">
                <h3
                    class="text-sm font-medium"
                    :class="colorScheme.title"
                >
                    {{ title }}
                </h3>
                <div
                    class="mt-2 text-sm"
                    :class="colorScheme.mainText"
                >
                    <ul
                        role="list"
                        class="list-disc space-y-1 pl-5"
                    >
                        <li
                            v-for="(li, i) in listItems"
                            :key="i"
                        >
                            {{ li }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
    <div
        v-if="type == 'actions'"
        class="rounded-md p-4"
        :class="colorScheme.background"
    >
        <div class="flex">
            <div class="shrink-0">
                <CheckCircleIcon
                    class="h-5 w-5"
                    :class="colorScheme.icon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3">
                <h3
                    class="text-sm font-medium"
                    :class="colorScheme.title"
                >
                    {{ title }}
                </h3>
                <div
                    class="mt-2 text-sm"
                    :class="colorScheme.mainText"
                >
                    <p>{{ message }}</p>
                </div>
                <div class="mt-4">
                    <div class="-mx-2 -my-1.5 flex">
                        <button
                            v-for="(button, i) in buttons"
                            :key="i"
                            type="button"
                            class="rounded-md px-2 py-1.5 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                            :class="colorScheme.bottomLinks"
                            @click="button.action()"
                        >
                            {{ button.text }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div
        v-if="type == 'link'"
        class="rounded-md p-4"
        :class="colorScheme.background"
    >
        <div class="flex">
            <div class="shrink-0">
                <InformationCircleIcon
                    class="h-5 w-5"
                    :class="colorScheme.icon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
                <p
                    class="text-sm"
                    :class="colorScheme.title"
                >
                    {{ title }}
                </p>
                <p class="mt-3 text-sm md:ml-6 md:mt-0">
                    <a
                        href="#"
                        class="whitespace-nowrap font-medium"
                        :class="colorScheme.rightLink"
                    >
                        Details
                        <span aria-hidden="true"> &rarr;</span>
                    </a>
                </p>
            </div>
        </div>
    </div>
    <div
        v-if="type == 'dismissable'"
        class="rounded-md p-4"
        :class="colorScheme.background"
    >
        <div class="flex items-start">
            <div class="shrink-0">
                <component
                    :is="variant == 'success' ? CheckCircleIcon : ExclamationCircleIcon"
                    class="h-5 w-5"
                    :class="colorScheme.icon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3 min-w-0">
                <h3
                    class="text-sm font-medium"
                    :class="colorScheme.title"
                >
                    {{ title }}
                </h3>
                <p
                    v-if="message"
                    class="mt-1 text-sm"
                    :class="colorScheme.mainText"
                >
                    {{ message }}
                </p>
                <button
                    v-if="details"
                    type="button"
                    class="mt-1 text-xs underline"
                    :class="colorScheme.mainText"
                    @click="showDetails = !showDetails"
                >
                    {{ showDetails ? "Hide details" : "Show details" }}
                </button>
                <p
                    v-if="details && showDetails"
                    class="mt-1 text-xs font-mono break-all"
                    :class="colorScheme.mainText"
                >
                    {{ details }}
                </p>
            </div>
            <div class="ml-auto pl-3">
                <div class="-mx-1.5 -my-1.5">
                    <button
                        type="button"
                        class="inline-flex rounded-md p-1.5 focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                        :class="colorScheme.dismissButton"
                        @click="emit('close')"
                    >
                        <span class="sr-only">Dismiss</span>
                        <XMarkIcon
                            class="h-5 w-5"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { XCircleIcon, ExclamationTriangleIcon, ExclamationCircleIcon, CheckCircleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { computed, onBeforeMount, onMounted, ref } from "vue";

export interface AlertModel {
    type: "normal" | "list" | "actions" | "link" | "dismissable";
    variant: "information" | "alert" | "warning" | "success" | "failure";
    accent?: boolean;
    title: string;
    message?: string;
    buttons?: { text: string; action: () => void }[];
    link?: { text: string; to: string };
    listItems?: string[];
}

const props = withDefaults(
    defineProps<{
        type?: "normal" | "list" | "actions" | "link" | "dismissable";
        variant?: "information" | "alert" | "warning" | "success" | "failure";
        accent?: boolean;
        title: string;
        message?: string;
        details?: string;
        buttons?: { text: string; action: () => void }[];
        link?: { text: string; to: string };
        listItems?: string[];
    }>(),
    {
        type: "normal",
        variant: "information",
        listItems: undefined, //
        buttons: undefined, //
        message: undefined, //
        details: undefined,
        link: undefined, // These were to kill a bunch of warnings.
    }
);

const showDetails = ref(false);

const emit = defineEmits<{
    (event: "close"): void;
}>();

function checkRequirements(): boolean {
    switch (props.type) {
        case "normal":
            return checkRequirementItems(props.title, props.message);
        case "list":
            return checkRequirementItems(props.title, props.listItems);
        case "actions":
            return checkRequirementItems(props.title, props.message, props.buttons);
        case "link":
            return checkRequirementItems(props.title, props.link);
        case "dismissable":
            return checkRequirementItems(props.title);
        default:
            return checkRequirementItems(props.title, props.message);
    }
}

function checkRequirementItems(...requirements: unknown[]): boolean {
    let re = true;
    requirements.forEach((r) => {
        if (r == undefined) re = false;
        if (Array.isArray(r) && r.length == 0) re = false;
        if (typeof r == "string" && r.length == 0) re = false;
    });

    return re;
}

const colorScheme = computed(() => {
    switch (props.variant) {
        case "information":
            return {
                background: "bg-blue-50",
                icon: "text-blue-400",
                title: "text-blue-800",
                mainText: "text-blue-700",
                bottomLinks: "bg-blue-50 text-blue-800 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50",
                rightLink: "text-blue-700 hover:text-blue-600",
                accent: "border-blue-400",
                dismissButton: "bg-blue-50 text-blue-500 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50",
            };
        case "alert":
            return {
                background: "bg-yellow-50",
                icon: "text-yellow-400",
                title: "text-yellow-800",
                mainText: "text-yellow-700",
                bottomLinks: "bg-yellow-50 text-yellow-800 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50",
                rightLink: "text-yellow-700 hover:text-yellow-600",
                accent: "border-yellow-400",
                dismissButton: "bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50",
            };
        case "warning":
            return {
                background: "bg-red-50",
                icon: "text-red-400",
                title: "text-red-800",
                mainText: "text-red-700",
                bottomLinks: "bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50",
                rightLink: "text-red-700 hover:text-red-600",
                accent: "border-red-400",
                dismissButton: "bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50",
            };
        case "success":
            return {
                background: "bg-green-50",
                icon: "text-green-400",
                title: "text-green-800",
                mainText: "text-green-700",
                bottomLinks: "bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50",
                rightLink: "text-green-700 hover:text-green-600",
                accent: "border-green-400",
                dismissButton: "bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50",
            };
        case "failure":
            return {
                background: "bg-red-50",
                icon: "text-red-400",
                title: "text-red-800",
                mainText: "text-red-700",
                bottomLinks: "bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50",
                rightLink: "text-red-700 hover:text-red-600",
                accent: "border-red-400",
                dismissButton: "bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50",
            };
        default:
            return {
                background: "bg-blue-50",
                icon: "text-blue-400",
                title: "text-blue-800",
                mainText: "text-blue-700",
                bottomLinks: "bg-blue-50 text-blue-800 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50",
                rightLink: "text-blue-700 hover:text-blue-600",
                accent: "border-blue-400",
                dismissButton: "bg-blue-50 text-blue-500 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50",
            };
    }
});

onBeforeMount(() => {
    if (!checkRequirements()) throw "Alert bar is missing some required items for the type selected.";
});
</script>
